import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { map, takeUntil } from 'rxjs';

import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { InboxService, InboxTabState } from './../../../core/services/inbox.service';
import { PatientService } from './../../../core/services/patient.service';
import {
    InboxCounts,
    InboxItemDto,
    InboxItemPrimaryAction,
    InboxItemTypeDescriptor,
    InboxTab,
    INBOX_TABS,
    INBOX_TAB_LABELS,
    inboxGroupDate,
    inboxItemType,
    inboxPrimaryAction,
    inboxSearchHint,
} from './../../../core/models/inbox-item.model';
import {
    DocumentReviewDialogComponent,
    DocumentReviewDialogData,
    DocumentReviewDialogResult,
} from './../../../shared/components/document-review-dialog/document-review-dialog.component';
import {
    ReferralNoteReviewDialogComponent,
    ReferralNoteReviewDialogData,
    ReferralNoteReviewDialogResult,
} from './referral-note-review-dialog/referral-note-review-dialog.component';
import {
    ReferralIncomingDialogComponent,
    ReferralIncomingDialogData,
    ReferralIncomingDialogResult,
} from './referral-incoming-dialog/referral-incoming-dialog.component';
import { PatientReferralDialogComponent, PatientReferralDialogData } from '../patient-file/patient-referral-dialog/patient-referral-dialog.component';
import {
    ReferralPracticeEntryDialogComponent,
    ReferralPracticeEntryDialogData,
    ReferralPracticeEntryDialogResult,
} from './referral-practice-entry-dialog/referral-practice-entry-dialog.component';
import { ReferralOnwardNoticeDialogComponent, ReferralOnwardNoticeDialogData } from './referral-onward-notice-dialog/referral-onward-notice-dialog.component';
import { ReferralLinkRequestDialogComponent, ReferralLinkRequestDialogData } from './referral-link-request-dialog/referral-link-request-dialog.component';
import { ReferralDemographicsDialogComponent, ReferralDemographicsDialogData } from './referral-demographics-dialog/referral-demographics-dialog.component';
import { PatientAppHistoryDialogComponent, PatientAppHistoryDialogData } from './patient-app-history-dialog/patient-app-history-dialog.component';
import { TaskInboxDialogComponent, TaskInboxDialogData } from './task-inbox-dialog/task-inbox-dialog.component';
import { GroupService } from '../../../core/services/group.service';
import { stateUnitGroupIds } from '../../../chat/ui/state-mode-chats';
import { visibleInboxItems } from './inbox-state-mode';

/** One date header and the rows under it. Same shape `results-list` groups into. */
interface InboxGroup {
    date: string;
    items: InboxItemDto[];
}

/**
 * Opens one item in place. Returns the dialog it opened, so the page can hold the one-dialog limit
 * and re-page when the dialog reports a change. Every opener's dialog closes with
 * `{ changed?: boolean }` (or nothing).
 */
type InboxOpener = (item: InboxItemDto) => MatDialogRef<unknown, { changed?: boolean } | undefined>;

/**
 * The Inbox — a group-level worklist for everything arriving from outside the practice.
 *
 * `tasks/inbox.md`. Routed at `patients/inbox` from `patient.module.ts`, beside `patients/results`,
 * whose shell this borrows: `mat-toolbar.action-toolbar`, a sticky `.filter-wrapper`, a
 * `mat-tab-group` with `matTabGroupGesture`, and date-grouped `mat-list-item`s.
 *
 * ── WHAT THIS DOES DIFFERENTLY FROM `results-list`, AND WHY ──────────────────────────
 *
 * 1. **The rows are SERVER-paged.** `results-list` holds every lab order in the practice and
 *    filters it into five tabs in memory; the sidenav then counts the same array again. This asks
 *    for one tab at a time and takes its counts from four `COUNT(*)`s
 *    (`tasks/prod-slowdown-db-ceiling.md`).
 *
 * 2. **The tab is in the URL.** `results-list` keeps `selectedTabIndex` as a plain field, so a
 *    reload — or a round trip out to a patient file and back — always lands on tab 0. On a
 *    worklist somebody leaves and returns to constantly, that is a small cut taken fifty times a
 *    day. `?tab=for-review` costs one `queryParamMap` subscription.
 *
 * 3. **The deep link is READ.** `results-list` navigates to the patient file with
 *    `queryParams: { labOrder: orderId }` and NOTHING ANYWHERE READS IT — the row opens the file
 *    and the reader is left to find the order themselves. This page opens the item in a dialog
 *    instead, and `?item=<itemType>:<sourceId>` reopens it after a reload. A link that does not
 *    work is worse than no link, because it is indistinguishable from one that does until you
 *    follow it.
 *
 * 4. **Search says what it covered.** Patient names are encrypted with no blind index, so the
 *    server can only match the document name and patient number. The rest is filtered here, over
 *    the loaded page, and the hint under the box says so rather than implying a practice-wide
 *    search that silently misses.
 */
@Component({
    selector: 'app-inbox',
    templateUrl: './inbox.component.html',
    styleUrls: ['./inbox.component.scss'],
})
export class InboxComponent extends BaseComponent implements OnInit, OnDestroy {
    readonly tabs = INBOX_TABS;
    readonly tabLabels = INBOX_TAB_LABELS;

    selectedTabIndex = 0;
    filterValue = '';

    counts: InboxCounts = { 'new': 0, 'for-review': 0, flagged: 0, pending: 0 };

    /** Per-tab view state, keyed by tab. The template never touches the service directly. */
    state: Record<string, InboxTabState> = {};
    /** Per-tab, date-grouped and text-filtered rows — what the template actually draws. */
    groups: Record<string, InboxGroup[]> = {};

    /** Ids with a flag write in flight, so a double click cannot post twice. */
    busyItemIds = new Set<string>();

    /**
     * The `?item=` the page opened with, held until its tab has loaded.
     *
     * The row has to be IN HAND before its dialog can open — the dialog needs the item to know
     * which stage it is at. So this is consumed by `rebuild`, not by the query-param subscription.
     */
    private pendingItemKey: string | null = null;

    /**
     * The open review dialog, or null — and ONE is the limit.
     *
     * Not defensive tidiness; it closes a real loop. `openItem` writes `?item=` into the URL so a
     * reload or a shared link comes back to the same document. That write re-emits
     * `queryParamMap`, which re-arms `pendingItemKey` from it, and `consumePendingItem` then opens
     * the item again the next time rows arrive — which flagging from inside the dialog does
     * immediately, because a flag re-reads the Flagged tab and the counts. The result was a second
     * dialog stacking on top of the one the user was working in.
     *
     * Clearing the key is not enough on its own: the URL is the thing re-arming it, and dropping
     * the URL write would cost the deep link. Guarding the dialog covers every route to a second
     * one, including any future caller.
     */
    private reviewDialogRef: MatDialogRef<unknown, { changed?: boolean } | undefined> | null = null;

    /**
     * ITEM TYPE → how its row opens. `tasks/referrals.md` §5.6.
     *
     * Everything else on this page is already type-neutral; this was the one place that named
     * `document`. A source whose adapter ships on the server before its opener ships here still draws,
     * and `openItem` says it cannot be opened yet rather than opening a dialog that cannot read it.
     *
     * Adding a type is one entry: a function that opens its dialog and returns the ref.
     */
    private readonly openers: Record<string, InboxOpener> = {
        document: (item) => this.openDocument(item),
        'referral-note': (item) => this.openReferralNote(item),
        'referral-incoming': (item) => this.openReferralIncoming(item),
        'referral-practice-entry': (item) => this.openPracticeEntry(item),
        'referral-onward-notice': (item) => this.dialog.open(ReferralOnwardNoticeDialogComponent, {
            width: '520px', maxWidth: '96vw', autoFocus: false,
            data: { noticeId: item.sourceId, groupId: item.groupId, patientName: item.patientName ?? null } as ReferralOnwardNoticeDialogData,
        }),
        'referral-demographics': (item) => this.dialog.open(ReferralDemographicsDialogComponent, {
            width: '760px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false,
            data: { proposalId: item.sourceId, groupId: item.groupId, patientName: item.patientName ?? null } as ReferralDemographicsDialogData,
        }),
        // The patient app (tasks/patient-app.md §6.5, §5.2): the same two reviews as for another practice.
        'patient-app-entry': (item) => this.dialog.open(ReferralPracticeEntryDialogComponent, {
            width: '720px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false,
            data: { entryId: item.sourceId, groupId: item.groupId, patientName: item.patientName ?? null, source: 'patient-app' } as ReferralPracticeEntryDialogData,
        }),
        'patient-app-demographics': (item) => this.dialog.open(ReferralDemographicsDialogComponent, {
            width: '760px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false,
            data: { proposalId: item.sourceId, groupId: item.groupId, patientName: item.patientName ?? null, source: 'patient-app' } as ReferralDemographicsDialogData,
        }),
        'patient-app-history': (item) => this.dialog.open(PatientAppHistoryDialogComponent, {
            width: '760px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false,
            data: { proposalId: item.sourceId, groupId: item.groupId, patientName: item.patientName ?? null } as PatientAppHistoryDialogData,
        }),
        'referral-link-request': (item) => this.dialog.open(ReferralLinkRequestDialogComponent, {
            width: '520px', maxWidth: '96vw', autoFocus: false,
            data: { linkId: item.sourceId, groupId: item.groupId, title: item.title } as ReferralLinkRequestDialogData,
        }),
        task: (item) => this.dialog.open(TaskInboxDialogComponent, {
            width: '520px', maxWidth: '96vw', autoFocus: false,
            data: { taskId: item.sourceId, patientName: item.patientName ?? null } as TaskInboxDialogData,
        }),
    };

    constructor(
        private inboxService: InboxService,
        private patientService: PatientService,
        private router: Router,
        private route: ActivatedRoute,
        private groupService: GroupService,
        fs: FeedbackService,
        md: MatDialog,
    ) {
        super(fs, md);
    }

    /** The user's practices that are state units: their referral and patient app items are not shown. */
    private unitGroupIds = new Set<string>();

    ngOnInit(): void {
        this.groupService.userGroups$.pipe(takeUntil(this.destroy$)).subscribe((groups) => (this.unitGroupIds = stateUnitGroupIds(groups)));
        for (const tab of this.tabs) {
            this.state[tab] = this.inboxService.stateOf(tab);
            this.groups[tab] = [];
            this.inboxService.state$(tab)
                .pipe(takeUntil(this.destroy$))
                .subscribe((state) => {
                    this.state[tab] = state;
                    this.rebuild(tab);
                });
        }

        this.inboxService.counts$
            .pipe(takeUntil(this.destroy$))
            .subscribe((counts) => { this.counts = counts; });

        // The URL is the source of truth for which tab is open, so this handles the first paint,
        // a reload, and the browser Back button with one subscription rather than three.
        this.route.queryParamMap
            .pipe(takeUntil(this.destroy$))
            .subscribe((params) => {
                const requested = params.get('tab') as InboxTab | null;
                const index = requested ? this.tabs.indexOf(requested) : -1;
                const next = index === -1 ? 0 : index;
                const changed = next !== this.selectedTabIndex;
                this.selectedTabIndex = next;

                const item = params.get('item');
                if (item) this.pendingItemKey = item;

                if (changed || !this.state[this.activeTab].page) this.refresh();
                this.inboxService.setActiveTab(this.activeTab);
            });
    }

    override ngOnDestroy(): void {
        // Nobody is looking at the Inbox any more, so a socket event should refresh the COUNTS
        // (the badge stays honest wherever the user is) and not re-page rows nothing is drawing.
        this.inboxService.setActiveTab(null);
        super.ngOnDestroy();
    }

    get activeTab(): InboxTab {
        return this.tabs[this.selectedTabIndex] || 'new';
    }

    /* ── Tabs ───────────────────────────────────────────────────────────────────────────── */

    /**
     * Writes the tab to the URL and lets the `queryParamMap` subscription do the rest.
     *
     * `replaceUrl` so that working through four tabs does not put four entries in the history and
     * make Back mean "the tab before this one" four times over.
     */
    onTabChange(index: number): void {
        const tab = this.tabs[index] || 'new';
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab, item: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    countFor(tab: InboxTab): number {
        return (this.counts as any)[tab] || 0;
    }

    /**
     * The tab's label.
     *
     * A method rather than `tabLabels[tab]` in the template: the `let-tab` of an `ng-template`
     * is typed `any`, and indexing a `Record<InboxTab, string>` with it is an error under strict
     * template checking. Widening the map's key type to `string` would have silenced it by
     * throwing away the exhaustiveness that makes a missing label a compile error.
     */
    labelFor(tab: string): string {
        return (this.tabLabels as Record<string, string>)[tab] || tab;
    }

    /* ── Loading ────────────────────────────────────────────────────────────────────────── */

    refresh(): void {
        this.inboxService.load(this.activeTab, this.filterValue)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        this.inboxService.loadCounts().pipe(takeUntil(this.destroy$)).subscribe();
    }

    loadMore(): void {
        this.inboxService.loadMore(this.activeTab, this.filterValue)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }

    /**
     * The search box.
     *
     * Re-queries the SERVER rather than filtering in place, because the plaintext half of the
     * search — document name and patient number — spans the whole practice and not just the rows
     * already loaded. The encrypted half is then applied to what comes back, in `rebuild`.
     */
    applyFilter(): void {
        this.refresh();
    }

    clearFilter(): void {
        this.filterValue = '';
        this.refresh();
    }

    searchHint(): string {
        return inboxSearchHint(this.state[this.activeTab] ? this.state[this.activeTab].page : null);
    }

    /* ── Grouping and the client half of the filter ─────────────────────────────────────── */

    /**
     * Date-group one tab's rows, after applying the half of the search the server could not.
     *
     * `Patient.name` and `.surname` are encrypted with no blind index, so no `WHERE` can match
     * them — this is the only place a name search can happen at all, and it therefore narrows the
     * LOADED PAGE rather than the practice. The hint under the search box says exactly that.
     */
    private rebuild(tab: InboxTab): void {
        const items = this.applyNameFilter(visibleInboxItems(this.state[tab].items || [], this.unitGroupIds));

        const grouped = _.groupBy(items, (item) => {
            const date = inboxGroupDate(item, tab);
            return date && moment(date).isValid() ? moment(date).format('YYYY-MM-DD') : '';
        });

        this.groups[tab] = _.chain(grouped)
            .map((value, key) => ({ date: key, items: value }))
            // The SERVER already ordered these; this only orders the GROUPS. Re-sorting the rows
            // within a group would fight the keyset order and make paging look non-deterministic.
            .orderBy((group) => group.date, 'desc')
            .value();

        this.consumePendingItem(tab, items);
    }

    private applyNameFilter(items: InboxItemDto[]): InboxItemDto[] {
        const term = this.filterValue.trim().toLowerCase();
        if (!term) return items;
        const terms = term.split(' ').filter(Boolean);
        return items.filter((item) => terms.every((part) =>
            _.includes((item.patientName || '').toLowerCase(), part)
            || _.includes((item.patientNumber || '').toLowerCase(), part)
            || _.includes((item.title || '').toLowerCase(), part)));
    }

    /** Opens the `?item=` the page arrived with, once its row is actually in hand. */
    private consumePendingItem(tab: InboxTab, items: InboxItemDto[]): void {
        if (!this.pendingItemKey || tab !== this.activeTab) return;
        const match = items.find((item) => this.keyOf(item) === this.pendingItemKey);
        if (!match) return;
        this.pendingItemKey = null;
        this.openItem(match);
    }

    /* ── Rows ───────────────────────────────────────────────────────────────────────────── */

    keyOf(item: InboxItemDto): string {
        return `${item.itemType}:${item.sourceId}`;
    }

    /** `trackBy` on the composite key, so a reload does not tear down and rebuild every row. */
    trackItem = (_index: number, item: InboxItemDto): string => this.keyOf(item);

    trackGroup = (_index: number, group: InboxGroup): string => group.date;

    /** The type icon. Falls back rather than breaking on a source this build has no icon for. */
    typeOf(item: InboxItemDto): InboxItemTypeDescriptor {
        return inboxItemType(item.itemType);
    }

    /** The row's button for any item type: documents via their review rule, others from the server. */
    actionFor(item: InboxItemDto): InboxItemPrimaryAction | null {
        return inboxPrimaryAction(item, this.activeTab);
    }

    isFlagged(item: InboxItemDto): boolean {
        return !!item.flaggedAt;
    }

    isBusy(item: InboxItemDto): boolean {
        return this.busyItemIds.has(this.keyOf(item));
    }

    /* ── Actions ────────────────────────────────────────────────────────────────────────── */

    /**
     * Open the item for review, IN PLACE.
     *
     * This is the point of the page. Working a queue of thirty returns by routing out to a patient
     * file and back thirty times is thirty full file loads to make thirty one-click decisions —
     * which is what the alternative design would have cost, and why it was not chosen.
     *
     * The dialog is `shared/components/document-review-dialog`, shared with the patient-file panel
     * so there is ONE implementation of sign-off and validation. It refuses on its own if the
     * caller may not act; `actionFor` only decides whether to draw a button.
     */
    openItem(item: InboxItemDto, event?: Event): void {
        /*
         * STOPPED HERE, because the row is clickable too.
         *
         * `.inbox-row` carries `(click)="openItem(item)"` so anywhere on the row opens it. The
         * Sign off / Validate button sits INSIDE that row and called the same method without the
         * event, so one click ran this twice — the button's handler, then the row's as it bubbled
         * — and the user got two dialogs stacked on top of each other.
         *
         * `openPatientFile` and `toggleFlag` beside it already took the event for exactly this
         * reason; this one was the odd button out.
         */
        if (event) event.stopPropagation();

        // Already reviewing something: see `reviewDialogRef`.
        if (this.reviewDialogRef) return;

        const opener = this.openers[item.itemType];
        if (!opener) {
            // A source whose adapter has shipped on the server before its client affordance has.
            // Say so rather than opening a dialog that cannot read it.
            this.showToastMessage('This item cannot be opened here yet.');
            return;
        }

        // So a reload — or a share of the URL — comes back to the same open item.
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { item: this.keyOf(item) },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });

        this.reviewDialogRef = opener(item);

        this.reviewDialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: { changed?: boolean } | undefined) => {
                this.reviewDialogRef = null;
                // Drop `?item=` whatever happened, so a reload does not reopen a dialog the user
                // has just dismissed.
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { item: null },
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                });
                // A decision moves the item to another tab, so this one is re-paged. The socket
                // event does the same thing a moment later; doing it here too means the row does
                // not linger for the length of a round trip on the screen that just acted on it.
                if (result && result.changed) this.refresh();
            });
    }

    /** The `document` opener: the shared review dialog, exactly as `openItem` opened it before openers existed. */
    private openDocument(item: InboxItemDto): MatDialogRef<DocumentReviewDialogComponent, DocumentReviewDialogResult> {
        const data: DocumentReviewDialogData = {
            requestId: item.sourceId,
            // §32 — the practice whose worklist this row came off, which is the one the URL named.
            groupId: item.groupId,
            patientId: item.patientId || null,
            patientName: item.patientName || null,
            // So a reviewer can mark this for attention without closing it and hunting the row.
            flag: this.flagContextFor(item),
        };

        return this.dialog.open(DocumentReviewDialogComponent, {
            width: '760px', maxWidth: '96vw', maxHeight: '92vh',
            autoFocus: false, data,
        });
    }

    /** The `referral-note` opener: "Validate to timeline" for an external clinician's note (tasks/referrals.md §5.5). */
    private openReferralNote(item: InboxItemDto): MatDialogRef<ReferralNoteReviewDialogComponent, ReferralNoteReviewDialogResult> {
        const data: ReferralNoteReviewDialogData = { entryId: item.sourceId, groupId: item.groupId };
        return this.dialog.open(ReferralNoteReviewDialogComponent, {
            width: '720px', maxWidth: '96vw', maxHeight: '92vh',
            autoFocus: false, data,
        });
    }

    /**
     * The `referral-incoming` opener (referrals architecture §19, P5): accept or decline a referral from another practice.
     * A summary share-back chosen on accepting is written next, in the patient's referral dialog, opened onto it.
     */
    private openReferralIncoming(item: InboxItemDto): MatDialogRef<ReferralIncomingDialogComponent, ReferralIncomingDialogResult> {
        const data: ReferralIncomingDialogData = { receiptId: item.sourceId, groupId: item.groupId };
        const ref = this.dialog.open(ReferralIncomingDialogComponent, { width: '760px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false, data });
        ref.afterClosed().subscribe((result) => {
            if (!result?.summaryShareBack) return;
            const shareBack = result.summaryShareBack;
            const referralData: PatientReferralDialogData = {
                patientId: shareBack.patientId, patientName: shareBack.patientName, canRefer: true,
                shareBack: { linkId: shareBack.linkId, replyToReceiptId: shareBack.receiptId, shareMode: 'summary', practiceName: shareBack.practiceName },
            };
            this.dialog.open(PatientReferralDialogComponent, { width: '760px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false, data: referralData });
        });
        return ref;
    }

    /** The `referral-practice-entry` opener (§19, P7): "Validate to timeline" for an entry another practice shared. */
    private openPracticeEntry(item: InboxItemDto): MatDialogRef<ReferralPracticeEntryDialogComponent, ReferralPracticeEntryDialogResult> {
        const data: ReferralPracticeEntryDialogData = { entryId: item.sourceId, groupId: item.groupId, patientName: item.patientName ?? null };
        return this.dialog.open(ReferralPracticeEntryDialogComponent, { width: '720px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false, data });
    }

    /** The patient file, for when the reviewer needs the whole record and not just this item. */
    openPatientFile(event: Event, item: InboxItemDto): void {
        event.stopPropagation();
        if (!item.patientId) {
            this.showToastMessage('This item is not linked to a patient file.');
            return;
        }
        const patientId = item.patientId;
        this.patientService.getPatientById(patientId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    /*
                     * QUERY PARAMS ARE KEPT, and `item` is dropped.
                     *
                     * A `router.navigate` drops the query string unless told otherwise, so opening
                     * a patient file from the Flagged tab lost `?tab=flagged` and the Inbox
                     * underneath silently reverted to New — which is what the user saw as "the tabs
                     * get switched when I open a patient file". The tab is the one thing that has
                     * to survive, because `returnToParent` reads it back out of the URL to know
                     * where to return to.
                     *
                     * `item` is nulled deliberately: it is the open-review-dialog deep link, and
                     * carrying it would re-open a dialog over the Inbox the moment we come back.
                     */
                    this.router.navigate(['/patients', { outlets: { patient: ['file', patientId] } }], {
                        queryParams: { item: null },
                        queryParamsHandling: 'merge',
                    });
                },
                error: () => {
                    this.showToastMessage('There was an error loading the patient file. Please try again.');
                },
            });
    }

    /**
     * Flag or unflag, from any tab.
     *
     * Flagging is an ANNOTATION and not a move: the row stays where it is and gains an icon. Only
     * the Flagged tab and the counts are re-read, which the service handles.
     */
    /**
     * The flag context handed to the review dialog, so a reviewer can mark a document for
     * attention WITHOUT closing it (§the dialog's own `DocumentReviewDialogData.flag` note).
     *
     * Shares `flagRequest` with the row's own flag button, so the two cannot drift into flagging
     * different things or reporting failure differently.
     */
    private flagContextFor(item: InboxItemDto) {
        return {
            flagged: this.isFlagged(item),
            toggle: () => {
                const wasFlagged = this.isFlagged(item);
                return this.flagRequest(item, wasFlagged).pipe(map(() => !wasFlagged));
            },
        };
    }

    /** Flag or unflag one item. The row button and the dialog both go through here. */
    private flagRequest(item: InboxItemDto, flagged: boolean) {
        return flagged
            ? this.inboxService.unflag(item.itemType, item.sourceId)
            : this.inboxService.flag(item.itemType, item.sourceId, null);
    }

    toggleFlag(event: Event, item: InboxItemDto): void {
        event.stopPropagation();
        const key = this.keyOf(item);
        if (this.busyItemIds.has(key)) return;
        this.busyItemIds.add(key);

        const done = () => this.busyItemIds.delete(key);
        const request = this.flagRequest(item, this.isFlagged(item));

        request.pipe(takeUntil(this.destroy$)).subscribe({
            next: (result: any) => {
                done();
                if (!result || !result.success) {
                    this.showToastMessage('Could not update the flag. Please try again.');
                }
            },
            error: () => {
                done();
                this.showToastMessage('Could not update the flag. Please try again.');
            },
        });
    }
}
